import { appEnv } from '../config';
import type {
  Customer,
  Question,
  DashboardResponse,
  QuestionHistory,
  LinkStatus,
  PublicLinkResponse,
  AnswerValue,
} from '../types';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

let currentAccessToken: string | undefined;

/** Called by AuthContext whenever the session changes, so the client always has a fresh token without prop-drilling it everywhere. */
export function setAccessToken(token: string | undefined): void {
  currentAccessToken = token;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('content-type', 'application/json');
  if (currentAccessToken) {
    headers.set('authorization', `Bearer ${currentAccessToken}`);
  }

  const response = await fetch(`${appEnv.apiBaseUrl}${path}`, { ...options, headers });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const body = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const message = body?.message || body?.error || `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return body as T;
}

// --- Admin: dashboard ------------------------------------------------------
export const getDashboard = () => request<DashboardResponse>('/admin/dashboard');

// --- Admin: customers -------------------------------------------------------
export const listCustomers = () => request<{ customers: Customer[] }>('/admin/customers');
export const getCustomer = (id: string) => request<{ customer: Customer }>(`/admin/customers/${id}`);
export const createCustomer = (input: {
  companyName: string;
  contactName: string;
  contactEmail: string;
  notes?: string;
}) => request<{ customer: Customer }>('/admin/customers', { method: 'POST', body: JSON.stringify(input) });
export const updateCustomer = (id: string, input: Partial<Omit<Customer, 'assignedQuestionIds'>> & { assignedQuestionIds?: string[] | null }) =>
  request<{ customer: Customer }>(`/admin/customers/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
export const archiveCustomer = (id: string) =>
  request<void>(`/admin/customers/${id}/archive`, { method: 'POST' });
export const requestLogoUpload = (id: string, contentType: string, fileSizeBytes: number) =>
  request<{ uploadUrl: string; logoUrl: string }>(`/admin/customers/${id}/logo`, {
    method: 'POST',
    body: JSON.stringify({ contentType, fileSizeBytes }),
  });

/**
 * Uploads a logo file directly to S3 using a presigned PUT URL obtained
 * from requestLogoUpload. Bypasses the `request()` helper entirely - this
 * is a raw same-origin-CORS PUT to S3, not a call to our own API, so it
 * must not carry the admin bearer token or JSON content-type header.
 */
export async function uploadLogoFile(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Failed to upload logo file.');
  }
}
export const deleteCustomer = (id: string) => request<void>(`/admin/customers/${id}`, { method: 'DELETE' });
export const getCustomerHistory = (id: string) =>
  request<{ questionHistories: QuestionHistory[] }>(`/admin/customers/${id}/history`);
/** Exports the customer's latest answers as a styled .xlsx workbook. */
export const exportCustomerUrl = (id: string) => `${appEnv.apiBaseUrl}/admin/customers/${id}/export`;

// --- Admin: questions --------------------------------------------------------
export const listQuestions = (includeArchived = false) =>
  request<{ questions: Question[] }>(`/admin/questions${includeArchived ? '?includeArchived=true' : ''}`);
export const createQuestion = (input: {
  text: string;
  helpText?: string;
  type: Question['type'];
  options?: string[];
  required: boolean;
  priorityGroup: string;
  category: string;
  group?: string;
}) => request<{ question: Question }>('/admin/questions', { method: 'POST', body: JSON.stringify(input) });
export const updateQuestion = (id: string, input: Partial<Question>) =>
  request<{ question: Question }>(`/admin/questions/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
export const archiveQuestion = (id: string) =>
  request<void>(`/admin/questions/${id}/archive`, { method: 'POST' });
export const reorderQuestions = (orderedQuestionIds: string[]) =>
  request<void>('/admin/questions/reorder', { method: 'POST', body: JSON.stringify({ orderedQuestionIds }) });

export interface ImportQuestionsResult {
  imported: number;
  failed: number;
  errors: { row: number; message: string }[];
}

/**
 * Uploads a CSV file's raw text to the import endpoint. Bypasses the
 * `request()` helper's JSON content-type default since the body here is
 * CSV text, not JSON - but still needs the admin bearer token, unlike the
 * S3 logo upload above.
 */
export async function importQuestionsCsv(csvText: string): Promise<ImportQuestionsResult> {
  const headers = new Headers({ 'content-type': 'text/csv' });
  if (currentAccessToken) headers.set('authorization', `Bearer ${currentAccessToken}`);

  const response = await fetch(`${appEnv.apiBaseUrl}/admin/questions/import`, {
    method: 'POST',
    headers,
    body: csvText,
  });
  const text = await response.text();
  const body = text ? JSON.parse(text) : undefined;
  if (!response.ok) {
    const message = body?.message || body?.error || `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }
  return body as ImportQuestionsResult;
}

// --- Admin: links ------------------------------------------------------------
export const listLinks = (customerId: string) =>
  request<{ links: LinkStatus[] }>(`/admin/customers/${customerId}/links`);
export const createLink = (customerId: string, expiresAt: string) =>
  request<{ token: string; expiresAt: string }>(`/admin/customers/${customerId}/links`, {
    method: 'POST',
    body: JSON.stringify({ expiresAt }),
  });
export const revokeLinks = (customerId: string) =>
  request<{ revokedCount: number }>(`/admin/customers/${customerId}/links/revoke`, { method: 'POST' });

// --- Public: customer link ---------------------------------------------------
export const getPublicLink = (token: string) => request<PublicLinkResponse>(`/q/${token}`);
export const saveDraft = (token: string, answers: Record<string, AnswerValue>) =>
  request<{ version: number; submittedAt: string }>(`/q/${token}/draft`, {
    method: 'POST',
    body: JSON.stringify({ answers }),
  });
export const submitAnswers = (token: string, answers: Record<string, AnswerValue>) =>
  request<{ version: number; submittedAt: string }>(`/q/${token}/submit`, {
    method: 'POST',
    body: JSON.stringify({ answers }),
  });
